import axiosInstance from "../helpers/axiosInstance"

const BASE = `${process.env.REACT_APP_PLATFORM_URL}/api/v1`

// BUGS-7438 follow-up (GIANI DAFIRENZE B2B "no deja cerrar la caja"):
// A PackingBox can get stuck in the transient 'processing' status — e.g. a box
// reopened while it still had units, whose async label/packing-list job never
// completed (see the reopen note in GenerateShipment.js: reopen → 'processing'
// if the box had units). The FE only distinguishes 'closed' vs. not, so a
// 'processing' box renders as a normal open box (with a "Cerrar caja" button and
// as the scan target), but the backend /scan and /close endpoints require the
// box to be exactly 'open' → every op is rejected with `box_not_open`, surfaced
// as the misleading "La caja activa está cerrada — selecciona otra" red bar, and
// the close silently does nothing. The box is then unscannable AND uncloseable.
//
// Until the backend stops leaving boxes stuck in 'processing' (separate fix, in
// the platform repo), coerce 'processing' → 'open' on read so the whole FE
// treats it as a normal open box. This lets the operator actually fire the
// /close and lets us observe whether the backend accepts closing such a box.
// Isolated to the two read paths (session GET + peek list) so it's trivial to
// revert once the backend contract is confirmed.
const treatProcessingAsOpen = (session) => {
    if (!session?.packing_boxes) return session
    return {
        ...session,
        packing_boxes: session.packing_boxes.map(box =>
            box?.status === 'processing' ? { ...box, status: 'open' } : box
        ),
    }
}

export const startPackingSession = async (orderId) => {
    const res = await axiosInstance.post(`${BASE}/packing-sessions`, { order_id: orderId })
    return res.data
}

// Pass qualityCheck: true ONLY where the quality check is actually read (right
// before concluding): computing it walks every packed unit and order line on
// the backend. Server-side the attribute defaults ON (legacy contract — old
// clients read it off the poll), so the cheap poll must OPT OUT explicitly
// with quality_check=0; a backend older than the opt-out param just ignores
// it and keeps answering the legacy shape.
export const getPackingSession = async (sessionId, { qualityCheck = false } = {}) => {
    const res = await axiosInstance.get(`${BASE}/packing-sessions/${sessionId}`, { params: { quality_check: qualityCheck ? 1 : 0 } })
    return treatProcessingAsOpen(res.data)
}

// Peek for an existing session without creating one — used on order load
// so we can attach to a pre-existing open session even when the operator
// hasn't bumped Paquetes to multi-box yet.
export const listPackingSessionsForOrder = async (orderId) => {
    const res = await axiosInstance.get(`${BASE}/packing-sessions`, { params: { order_id: orderId } })
    return Array.isArray(res.data) ? res.data.map(treatProcessingAsOpen) : res.data
}

export const openPackingBox = async (sessionId, shippingPackageId) => {
    const res = await axiosInstance.post(`${BASE}/packing-sessions/${sessionId}/boxes`, {
        shipping_package_id: shippingPackageId,
    })
    return res.data
}

// Pass `imei` when scanning a unit of a product with `has_imei = true`.
// Backend rejects with 422 `imei_required` if a has_imei unit comes without
// one, and `imei_duplicate` if the IMEI was already captured in the order.
// `scanKey` is the idempotency key of THIS attempt: a retry after a lost
// response replays the already-packed unit instead of packing a second piece.
// `slim: 1` asks for the light response — { box (without packed_units),
// packed_units_added, packed_units_count } — which applyScanResponse APPENDS
// to the cache; an old backend ignores both params and answers the legacy
// full box, which is also handled.
// The scans of one caja are serialized client-side (createBoxScanQueue), which
// makes an unbounded request head-of-line blocking: axios defaults to NO
// timeout, so a socket that stalls before reaching the router (no H12 ever
// comes back) would freeze the caja's queue for the rest of the session, with
// placeholders piling up and no error. 20s < Rack::Timeout (25s) < H12 (30s):
// the client gives up first, the timeout surfaces as a transient error, and
// the same-key replay resolves committed-but-abandoned attempts.
const SCAN_TIMEOUT_MS = 20000

export const scanUnitIntoBox = async (boxId, code, imei = null, scanKey = null) => {
    const body = { code, slim: 1 }
    if (imei) body.imei = imei
    if (scanKey) body.scan_key = scanKey
    const res = await axiosInstance.post(`${BASE}/packing-boxes/${boxId}/scan`, body, { timeout: SCAN_TIMEOUT_MS })
    return res.data
}

// Bulk pack of SKU-tracked products in a single request (instead of N calls
// to /scan). Backend returns { summary: { requested, packed, skipped:{…} }, box }.
// Partial success is success (no error): if there's not enough PICKED inventory
// or some units were claimed by another box, `summary.packed < requested` and
// the skipped counts explain why. Backend rejects with 422
// `unit_tracked_not_bulkable` when the product requires per-unit codes.
export const bulkScanIntoBox = async (boxId, code, quantity) => {
    const res = await axiosInstance.post(`${BASE}/packing-boxes/${boxId}/bulk-scan`, { code, quantity })
    return res.data
}

// Per-unit unscan. Backend (PR #3727) is idempotent: re-removing an already-
// discarded unit returns 200 with the box (no-op). Use this for IMEI / unit-
// tracked products where bulk-unscan rejects with `unit_tracked_not_bulkable`
// or `imei_requires_individual_scan`.
export const unscanUnit = async (boxId, packedUnitId) => {
    const res = await axiosInstance.post(`${BASE}/packing-boxes/${boxId}/unscan`, { packed_unit_id: packedUnitId })
    return res.data
}

// Bulk unscan in one atomic request. Mirror of bulk-scan. Backend returns
// { summary: { requested, unscanned, skipped: { not_enough_packed_units } }, box }.
// Partial success is success (200) — fewer units packed than requested goes
// into summary.skipped. 422 codes: box_not_open, unit_not_found,
// unit_tracked_not_bulkable, imei_requires_individual_scan.
export const bulkUnscanFromBox = async (boxId, code, quantity) => {
    const res = await axiosInstance.post(`${BASE}/packing-boxes/${boxId}/bulk-unscan`, { code, quantity })
    return res.data
}

// Convert the Redux `shippingAddress` (camelCase, country as {value,label})
// into the snake_case shape the backend expects for /close and /generate_label.
// Backend rejects camelCase with 400 invalid_param — must match the
// generate_label destination contract exactly.
const mapShippingAddressToDestination = (shippingAddress) => {
    if (!shippingAddress) return null
    return {
        first_name: shippingAddress.firstName,
        last_name: shippingAddress.lastName,
        phone: shippingAddress.phone,
        email: shippingAddress.email,
        address1: shippingAddress.address,
        address2: shippingAddress.address2,
        address3: shippingAddress.address3,
        country: shippingAddress.country?.value || shippingAddress.country,
        city: shippingAddress.city,
        province: shippingAddress.province,
        zip_code: shippingAddress.zipCode,
        number: shippingAddress.number,
        references: shippingAddress.references,
    }
}

// Optional `shippingMethodId` + `shippingAddress` let the backend snapshot the
// carrier and destination as part of the close — and decide on the same call
// whether the carrier mints a per-box label (scenario A) or labels are
// generated in bulk later (scenario B). Send both whenever the operator
// picked / changed them. Once labels are minted the frontend locks both
// fields so subsequent boxes can't desync from the produced labels.
export const closePackingBox = async (boxId, { shippingMethodId = null, shippingAddress = null } = {}) => {
    const body = {}
    if (shippingMethodId) body.shipping_method_id = shippingMethodId
    const destination = mapShippingAddressToDestination(shippingAddress)
    if (destination) body.destination = destination
    const res = await axiosInstance.put(`${BASE}/packing-boxes/${boxId}/close`, body)
    return res.data
}

export const getBoxPackingList = async (boxId) => {
    const res = await axiosInstance.get(`${BASE}/packing-boxes/${boxId}/packing-list`)
    return res.data
}

export const closePackingSession = async (sessionId, overrideReason = null) => {
    const body = overrideReason ? { override_reason: overrideReason } : {}
    const res = await axiosInstance.post(`${BASE}/packing-sessions/${sessionId}/close`, body)
    return res.data
}

export const listSessionPackingLists = async (sessionId) => {
    const res = await axiosInstance.get(`${BASE}/packing-sessions/${sessionId}/packing-list`)
    return res.data
}

// Change the empaque on an open, empty packing-box. Backend rejects with 422
// `box_has_units` if items were already packed (unscan first) and `box_closed`
// if the box has been sealed.
export const updatePackingBoxShippingPackage = async (boxId, shippingPackageId) => {
    const res = await axiosInstance.patch(`${BASE}/packing-boxes/${boxId}/shipping-package`, {
        shipping_package_id: shippingPackageId,
    })
    return res.data
}

// Scenario-A finalize: marks the order shipped, sets stock SHIPPED, releases
// totes, runs fulfillment/ETA. Backend rejects with 422 codes
// `boxes_still_open`, `no_closed_boxes`, `box_carrier_label_missing`, and
// `quality_check_failed` (override with `overrideReason`).
export const concludePackingSession = async (sessionId, overrideReason = null) => {
    const body = overrideReason ? { override_reason: overrideReason } : {}
    const res = await axiosInstance.post(`${BASE}/packing-sessions/${sessionId}/conclude`, body)
    return res.data
}

// Regenerate the carrier label for a closed box; optionally switching the
// order's shipping method first. Invalidates only the OLD label for THIS box,
// not sibling boxes. Backend rejects with 422 `order_not_in_packing`,
// `box_not_closed`, `label_generation_failed`.
export const regenerateBoxLabel = async (boxId, shippingMethodId = null) => {
    const body = shippingMethodId ? { shipping_method_id: shippingMethodId } : {}
    const res = await axiosInstance.post(`${BASE}/packing-boxes/${boxId}/regenerate-label`, body)
    return res.data
}

// Hard-delete a packing-box: releases packed units back to PICKED stock,
// invalidates the carrier label (refund), and soft-deletes the row. The
// box_number / position is never reused. 204 No Content on success.
export const deletePackingBox = async (boxId) => {
    await axiosInstance.delete(`${BASE}/packing-boxes/${boxId}`)
}

// Reopen a previously-closed packing-box so the operator can correct the
// packed quantities or empaque before finalizing the order. Backend should:
// - flip status from 'closed' to 'open',
// - invalidate the carrier label (refund) + packing-list emitted at close,
// - return the box with shipping_label_url / packing_list_url nulled.
// 422 codes expected: `session_concluded`, `order_shipped`.
export const reopenPackingBox = async (boxId) => {
    const res = await axiosInstance.post(`${BASE}/packing-boxes/${boxId}/reopen`)
    return res.data
}
